/**
 * Shared Field Registry Types
 * Generic type definitions for field consolidation across all domains
 */

/**
 * Supported data types for field definitions
 */
export type FieldDataType =
  'string' | 'number' | 'date' | 'boolean' | 'array' | 'object' | 'selection';

/**
 * Primitive types for array elements
 */
export type PrimitiveType = 'string' | 'number' | 'date' | 'object';

/**
 * Relation types for entity associations
 */
export type RelationType = 'one-to-many' | 'many-to-many';

/**
 * Data source for relational data
 */
export type DataSource = 'api' | 'static';

/**
 * Selection option for dropdown fields
 */
export interface SelectionOption {
  value: string;
  label: string;
}

/**
 * Relation configuration for many-to-many or one-to-many associations
 */
export interface RelationConfig {
  type: RelationType;
  entityName: string; // e.g., 'manufacturers', 'facilities'
  idField: string; // e.g., 'manufacturer_id'
  displayField: string; // e.g., 'manufacturer_name'
  preferredDisplayField?: string; // e.g., 'preferred_product_name' for fallback
  statusField?: string; // e.g., 'manufacturing_status' for additional metadata
  dataSource: DataSource;
  apiEndpoint?: string;
  searchField?: string; // Field to search on for async select (defaults to displayField)
}

/**
 * Validation rules for field values
 */
export interface ValidationRules {
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  min?: number;
  max?: number;
}

/**
 * Which user-facing surfaces a field appears on. Drives the unified product
 * field registry (issue #585) — grid columns, Manage Columns order, Advanced
 * Search field list, the product modal, and Excel export all derive from
 * one field's `surfaces` membership instead of independently-maintained lists.
 */
export type FieldSurface = 'grid' | 'search' | 'modal' | 'export';

/**
 * Rendering configuration for UI display
 */
export interface RenderConfig {
  width?: number; // Column width in pixels
  minWidth?: number; // Minimum column width in pixels
  flex?: number; // Flex grow factor
  rows?: number; // For textarea fields
  maxDisplayLength?: number; // For truncation in DataGrid
  maxDisplayItems?: number; // For array truncation
  // 'dateonly' = SQL DATE-style value shown as the raw calendar date (no time,
  // no time zone conversion); 'datetime' = UTC timestamp shown in the viewer's zone.
  formatAs?: 'html' | 'text' | 'datetime' | 'dateonly';
}

/**
 * Base field definition with comprehensive metadata
 * Generic type parameter TEntity allows type-safe field key references
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic default must be structurally compatible with every domain entity (Product, Facility, Manufacturer, etc.) when the type param is omitted (e.g. `BaseFieldDefinition[]` field registries); Record<string, unknown> is not a supertype of concrete entity shapes and breaks assignability, unlike `any`
export interface BaseFieldDefinition<TEntity = any> {
  // Core identifiers
  key: keyof TEntity | string; // Database column name or display key
  label: string; // Human-readable label
  description?: string; // Tooltip/help text

  // Data characteristics
  dataType: FieldDataType;
  primitiveType?: PrimitiveType; // For arrays, specifies element type

  // Behavioral flags
  isRequired?: boolean; // Field is mandatory
  isReadOnly?: boolean; // Field cannot be edited
  isSortable?: boolean; // Field can be sorted in DataGrid
  isFilterable?: boolean; // Field can be filtered in DataGrid
  isSearchable?: boolean; // Field appears in Advanced Search
  isEditable?: boolean; // Field can be edited in modal (for relations)

  // Computed field metadata
  isComputed?: boolean; // Field is calculated from other data
  computedFrom?: keyof TEntity; // Source field for computation

  // Relational metadata
  isRelation?: boolean; // Field represents a relation to other entities
  relationConfig?: RelationConfig;

  // Selection options (for static dropdowns)
  selectionOptions?: SelectionOption[];

  // Validation rules
  validation?: ValidationRules;

  // Rendering hints
  renderConfig?: RenderConfig;

  // Grouping/surface metadata (issue #585) — optional so existing domain
  // registries (manufacturers, facilities, grades, therapeutic-areas, audit)
  // compile unchanged; only the product domain's merged registry populates
  // these.
  group?: string; // Logical group name (e.g. 'CAS', 'Orange Book fields')
  surfaces?: FieldSurface[]; // Which surfaces this field appears on
}

/**
 * Type guard to check if a field is a relation
 */
export function isRelationField<TEntity>(
  field: BaseFieldDefinition<TEntity>
): field is BaseFieldDefinition<TEntity> & {
  isRelation: true;
  relationConfig: RelationConfig;
} {
  return field.isRelation === true && !!field.relationConfig;
}

/**
 * Type guard to check if a field is computed
 */
export function isComputedField<TEntity>(
  field: BaseFieldDefinition<TEntity>
): field is BaseFieldDefinition<TEntity> & {
  isComputed: true;
  computedFrom: keyof TEntity;
} {
  return field.isComputed === true && !!field.computedFrom;
}

/**
 * Type guard to check if a field has HTML content
 */
export function isHtmlField<TEntity>(
  field: BaseFieldDefinition<TEntity>
): boolean {
  return field.renderConfig?.formatAs === 'html';
}

/**
 * Type guard to check if a date field holds a date-only value (SQL DATE or
 * date-only semantics) that must be shown without time zone conversion
 */
export function isDateOnlyField<TEntity>(
  field: BaseFieldDefinition<TEntity>
): boolean {
  return field.renderConfig?.formatAs === 'dateonly';
}
